import { normalizePedagogicalPattern } from './core/patternVocabulary';
import { PEDAGOGICAL_VARIANTS } from './core/pedagogicalContractDiagnostics';

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function safeId(raw, idx) {
  const base = String(raw || `concept_${idx + 1}`)
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || `concept_${idx + 1}`;
}

function splitIntoSentences(value) {
  return (
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .match(/[^.!?]+[.!?]+|[^.!?]+$/g)
      ?.map((item) => item.trim()) || []
  );
}

function appendNarrativeChunk(chunks, value, maxChars) {
  const normalized = String(value || '').trim();
  if (!normalized) return;

  const current = chunks.join('\n');
  const withChunk = current ? `${current}\n${normalized}` : normalized;
  if (withChunk.length <= maxChars) {
    chunks.push(normalized);
    return;
  }

  const sentences = splitIntoSentences(normalized);
  if (sentences.length === 0) return;

  let partial = '';
  for (const sentence of sentences) {
    const candidate = partial ? `${partial} ${sentence}` : sentence;
    const candidateBody = current ? `${current}\n${candidate}` : candidate;
    if (candidateBody.length > maxChars) break;
    partial = candidate;
  }

  if (partial) {
    chunks.push(partial);
  }
}

function normalizeConcept(raw, idx) {
  const id = safeId(raw.id || raw.conceptId || raw.slug || raw.title || raw.label, idx);
  const title = String(raw.title || raw.label || raw.concept || `Concept ${idx + 1}`);
  const summary = String(raw.summary || raw.description || raw.explanation || '');
  const keyPoints = asArray(raw.keyPoints || raw.points || raw.bullets)
    .map(String)
    .slice(0, 4);
  const examples = asArray(raw.examples).map(String).slice(0, 3);
  const prerequisites = asArray(raw.prerequisites || raw.prereqIds || raw.dependsOn)
    .map((v) => safeId(v, idx))
    .filter(Boolean);
  const detail = raw.detail || raw.slideContent || null;

  return {
    id,
    title,
    summary,
    keyPoints,
    examples,
    prerequisites,
    detail,
  };
}

function buildNarrativeText(concept) {
  const detail = concept.detail || {};
  const detailKeyPoints = asArray(detail.keyPoints || detail.points)
    .map(String)
    .slice(0, 6);
  const detailExamples = asArray(detail.examples).map(String).slice(0, 4);
  const maxNarrativeChars = 1100;
  const lines = [];
  if (detail.bridgeFromPrevious)
    appendNarrativeChunk(lines, detail.bridgeFromPrevious, maxNarrativeChars);
  if (detail.explanation) {
    appendNarrativeChunk(lines, detail.explanation, maxNarrativeChars);
  } else if (concept.summary) {
    appendNarrativeChunk(lines, concept.summary, maxNarrativeChars);
  }
  const points = detailKeyPoints.length > 0 ? detailKeyPoints : concept.keyPoints;
  if (points.length > 0) {
    lines.push('');
    lines.push(...points.map((point) => `- ${point}`));
  }
  const examples = detailExamples.length > 0 ? detailExamples : concept.examples;
  if (examples.length > 0) {
    lines.push('');
    lines.push('Examples:');
    lines.push(...examples.map((example) => `* ${example}`));
  }
  if (detail.bridgeToNext) {
    lines.push('');
    appendNarrativeChunk(lines, `Next: ${String(detail.bridgeToNext)}`, maxNarrativeChars);
  }
  return lines.join('\n');
}

function buildSketchPathPayload(concept) {
  const sketch = concept.detail?.sketchPath;
  if (!sketch || !Array.isArray(sketch.nodes) || !Array.isArray(sketch.edges)) {
    return null;
  }

  const nodes = sketch.nodes
    .filter((n) => n && n.id != null && typeof n.xk === 'number' && typeof n.yk === 'number')
    .map((n) => ({
      id: String(n.id),
      label: String(n.label || n.id),
      xk: n.xk,
      yk: n.yk,
    }));

  const nodeIds = new Set(nodes.map((n) => n.id));
  const edges = sketch.edges
    .filter((e) => e && nodeIds.has(String(e.from)) && nodeIds.has(String(e.to)))
    .map((e) => ({
      from: String(e.from),
      to: String(e.to),
      label: e.label ? String(e.label) : '',
    }));

  if (nodes.length === 0 || edges.length === 0) {
    return null;
  }

  return {
    title: sketch.title || `${concept.title} Sketch`,
    nodes,
    edges,
    draw: {
      secPerEdge: sketch.draw?.secPerEdge || 0.45,
      secPerNode: sketch.draw?.secPerNode || 0.25,
      showArrowheads: sketch.draw?.showArrowheads ?? true,
      interactive: sketch.draw?.interactive ?? true,
    },
  };
}

function buildEquationPayload(concept) {
  const detail = concept.detail || {};
  const equations = asArray(detail.equations || detail.mathEquations || detail.formulas)
    .map((eq) => String(eq))
    .filter(Boolean)
    .slice(0, 6);

  if (equations.length === 0) return null;
  return {
    title: detail.equationTitle || `${concept.title} Equations`,
    equations,
    timing: {
      secPerLine: detail.equationTiming?.secPerLine || 0.9,
      charsPerSec: detail.equationTiming?.charsPerSec || 42,
    },
  };
}

function buildArtifactPayload(concept) {
  const artifacts = asArray(concept?.detail?.scene?.artifacts);
  if (artifacts.length === 0) return null;

  const allowedPrimitives = new Set([
    'circle',
    'line',
    'rect',
    'polyline',
    'arrow',
    'spring',
    'pendulum',
  ]);
  const allowedMotion = new Set([
    'free_fall',
    'bounce_ball',
    'light_pulse',
    'wavefront_expand',
    'pendulum_swing',
    'spring_oscillation',
  ]);

  const normalized = artifacts
    .filter((a) => a && allowedPrimitives.has(String(a.primitive || '').toLowerCase()))
    .slice(0, 8)
    .map((a, idx) => {
      const primitive = String(a.primitive).toLowerCase();
      const motionType = String(a?.motion?.type || '').toLowerCase();
      return {
        id: String(a.id || `${primitive}_${idx + 1}`),
        primitive,
        params: a.params || {},
        motion: allowedMotion.has(motionType) ? { ...a.motion, type: motionType } : undefined,
      };
    });

  return normalized.length > 0 ? normalized : null;
}

function buildPedagogicalDiagramPayload(concept) {
  const raw =
    concept?.detail?.pedagogicalDiagram ||
    concept?.detail?.teachingDiagram ||
    concept?.detail?.diagramSpec ||
    null;
  if (!raw || typeof raw !== 'object') return null;

  const variant = String(raw.variant || raw.type || '')
    .toLowerCase()
    .trim();
  const allowed = new Set(PEDAGOGICAL_VARIANTS);
  if (!allowed.has(variant)) return null;

  const normalized = {
    variant,
    pattern: normalizePedagogicalPattern(raw.pattern || ''),
    title: raw.title || `${concept.title} Diagram`,
    subtitle: raw.subtitle || '',
    durationSec: Number.isFinite(raw.durationSec) ? raw.durationSec : 6,
    shapes: Array.isArray(raw.shapes) ? raw.shapes.slice(0, 32) : [],
    characters: Array.isArray(raw.characters) ? raw.characters.slice(0, 8) : [],
    table: raw.table && typeof raw.table === 'object' ? raw.table : undefined,
    chart: raw.chart && typeof raw.chart === 'object' ? raw.chart : undefined,
    graph: raw.graph && typeof raw.graph === 'object' ? raw.graph : undefined,
    chat: raw.chat && typeof raw.chat === 'object' ? raw.chat : undefined,
    timeline: raw.timeline && typeof raw.timeline === 'object' ? raw.timeline : undefined,
    tree: raw.tree && typeof raw.tree === 'object' ? raw.tree : undefined,
    stackQueue: raw.stackQueue && typeof raw.stackQueue === 'object' ? raw.stackQueue : undefined,
    coordinatePlane:
      raw.coordinatePlane && typeof raw.coordinatePlane === 'object'
        ? raw.coordinatePlane
        : undefined,
    flowDiagram:
      raw.flowDiagram && typeof raw.flowDiagram === 'object' ? raw.flowDiagram : undefined,
    setDiagram: raw.setDiagram && typeof raw.setDiagram === 'object' ? raw.setDiagram : undefined,
    heatmap: raw.heatmap && typeof raw.heatmap === 'object' ? raw.heatmap : undefined,
    field: raw.field && typeof raw.field === 'object' ? raw.field : undefined,
  };

  // Repair minimal variant payload so renderer can still draw without collapsing to fallback behavior.
  if (variant === 'timeline' && !normalized.timeline) {
    normalized.timeline = { events: [{ id: 'e1', label: concept.title, atK: 0.1 }] };
  } else if (variant === 'tree' && !normalized.tree) {
    normalized.tree = {
      nodes: [{ id: 'root', label: concept.title, xk: 0.5, yk: 0.2 }],
      edges: [],
    };
  } else if (variant === 'stack_queue' && !normalized.stackQueue) {
    normalized.stackQueue = { mode: 'stack', items: [concept.title] };
  } else if (variant === 'coordinate_plane' && !normalized.coordinatePlane) {
    normalized.coordinatePlane = {
      points: [{ id: 'p1', label: concept.title, xk: 0.2, yk: 0.7 }],
      curves: [],
    };
  } else if (variant === 'flow_diagram' && !normalized.flowDiagram) {
    normalized.flowDiagram = {
      stages: [{ id: 's1', label: concept.title, xk: 0.2, yk: 0.5 }],
      links: [],
    };
  } else if (variant === 'set_diagram' && !normalized.setDiagram) {
    normalized.setDiagram = {
      sets: [{ id: 'a', label: concept.title, xk: 0.5, yk: 0.5, rk: 0.2 }],
    };
  } else if (variant === 'heatmap' && !normalized.heatmap) {
    normalized.heatmap = {
      labelsX: ['x1', 'x2'],
      labelsY: ['y1', 'y2'],
      values: [
        [0.2, 0.5],
        [0.7, 0.4],
      ],
    };
  } else if (variant === 'field' && !normalized.field) {
    normalized.field = {
      vectors: [{ id: 'v1', xk: 0.2, yk: 0.2, dxk: 0.05, dyk: -0.03 }],
    };
  } else if (variant === 'graph' && !normalized.graph) {
    normalized.graph = {
      nodes: [{ id: 'n1', label: concept.title, xk: 0.5, yk: 0.5 }],
      edges: [],
    };
  } else if (variant === 'table' && !normalized.table) {
    normalized.table = { headers: ['Concept', 'Value'], rows: [[concept.title, 'Key idea']] };
  } else if (variant === 'chart' && !normalized.chart) {
    normalized.chart = {
      type: 'line',
      labels: ['A', 'B', 'C'],
      series: [{ name: 'series', values: [0.2, 0.5, 0.8] }],
    };
  }

  return normalized;
}

function buildKnowledgeGraphSlide(concepts, lessonTitle, includeSceneSpec) {
  const nodes = concepts.map((concept) => ({ id: concept.id, label: concept.title }));
  const edges = [];
  for (const concept of concepts) {
    for (const prereqId of concept.prerequisites) {
      edges.push({ from: prereqId, to: concept.id });
    }
  }

  const root = concepts.find((c) => c.prerequisites.length === 0)?.id || concepts[0]?.id;

  return {
    id: 'knowledge_graph_overview',
    kind: 'BOXED_DEMO',
    deps: ['lesson_overview'],
    ...(includeSceneSpec
      ? {
          scene: {
            layout: { mode: 'canvas_based_dynamic' },
            animationPlan: [
              { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.3 },
              { step: 2, target: 'graph', effect: 'physics_expand', durationSec: 1.4 },
            ],
            timeline: { waitForAnimationPlan: true },
            physics: { enabled: true, mode: 'force_graph' },
            layers: { background: true, content: true, highlight: true, transition: true },
          },
        }
      : {}),
    payload: {
      title: 'Concept Map',
      paddingK: 0.05,
      child: {
        kind: 'PHYSICS_DIAGRAM',
        payload: {
          title: `${lessonTitle}: Concept Relationships`,
          subtitle: 'How key ideas connect and build on each other',
          durationSec: 7.0,
          layout: { mode: 'tree', rootId: root },
          physics: {
            nodeRadius: 20,
            springK: 8.5,
            damping: 0.86,
            repulsion: 9000,
            restLength: 86,
            anchorK: 2.2,
          },
          diagram: { nodes, edges },
        },
      },
    },
  };
}

function inferEventKindForNode(node) {
  if (!node || !node.kind) return 'REVEAL_TERM';
  if (node.kind === 'TABLE_FILL') return 'TABLE_FILL';
  if (node.kind === 'PHYSICS_DIAGRAM') return 'SIM_TICK';
  if (node.kind === 'DIAGRAM_STROKE') return 'GRAPH_TRAVERSAL';
  if (node.id === 'knowledge_graph_overview') return 'DEPENDENCY_LINK';
  if (node.id?.startsWith('focus_')) return 'COMPARE_STATE';
  if (node.kind === 'BOXED_DEMO') return 'GRAPH_TRAVERSAL';
  return 'REVEAL_TERM';
}

function buildInstructionalEvents(nodes) {
  if (!Array.isArray(nodes)) return [];
  return nodes.map((node, idx) => ({
    id: `evt_${node.id}`,
    kind: inferEventKindForNode(node),
    payload: {
      nodeId: node.id,
      nodeKind: node.kind,
      label:
        node.payload?.title ||
        node.payload?.label ||
        (typeof node.payload?.text === 'string' ? node.payload.text.slice(0, 80) : node.id),
      index: idx,
    },
    deps: Array.isArray(node.deps) ? node.deps.map((depId) => `evt_${depId}`) : [],
    checkpoint:
      idx === 0 ||
      node.id === 'knowledge_graph_overview' ||
      node.id === 'lesson_conclusion' ||
      String(node.id || '').startsWith('divider_'),
  }));
}

export function knowledgeGraphToLessonSpec(rawPayload, options = {}) {
  const graphList = rawPayload?.knowledgeGraph || rawPayload?.nodes || rawPayload?.concepts || [];
  const concepts = asArray(graphList)
    .map(normalizeConcept)
    .filter((c) => c.title);

  if (concepts.length === 0) {
    throw new Error('Knowledge graph payload is empty or invalid.');
  }

  const lessonId =
    options.lessonId || rawPayload.lessonId || `kg_${Date.now().toString(36).toLowerCase()}`;
  const lessonTitle = options.title || rawPayload.title || rawPayload.topic || 'Animated Lesson';
  const presentationMode = options.presentationMode === 'scene' ? 'scene' : 'visual';
  const includeSceneSpec = presentationMode === 'scene';
  const textScale = includeSceneSpec ? 0.5 : 1;
  const scaled = (cfg) => ({
    k: Number((cfg.k * textScale).toFixed(4)),
    min: Math.max(8, Math.round(cfg.min * textScale)),
  });

  const conceptSlides = [];
  let previousId = 'knowledge_graph_overview';

  concepts.forEach((concept, idx) => {
    const dividerId = `divider_${concept.id}`;
    const textId = `concept_${concept.id}`;
    const focusGraphId = `focus_${concept.id}`;

    const neighborhoodNodeMap = new Map();
    neighborhoodNodeMap.set(concept.id, { id: concept.id, label: concept.title, fixed: true });
    concept.prerequisites.forEach((prereqId) => {
      const prereqConcept = concepts.find((c) => c.id === prereqId);
      if (prereqConcept) {
        neighborhoodNodeMap.set(prereqConcept.id, {
          id: prereqConcept.id,
          label: prereqConcept.title,
        });
      }
    });

    const outgoing = concepts
      .filter((c) => c.prerequisites.includes(concept.id))
      .slice(0, 3)
      .map((c) => ({ id: c.id, label: c.title }));

    outgoing.forEach((c) => neighborhoodNodeMap.set(c.id, c));

    const neighborhoodNodes = Array.from(neighborhoodNodeMap.values());
    const neighborhoodEdges = [];
    concept.prerequisites.forEach((prereqId) =>
      neighborhoodEdges.push({ from: prereqId, to: concept.id })
    );
    outgoing.forEach((nextNode) => neighborhoodEdges.push({ from: concept.id, to: nextNode.id }));

    conceptSlides.push({
      id: dividerId,
      kind: 'DIVIDER',
      deps: [previousId],
      ...(includeSceneSpec
        ? {
            scene: {
              layout: { mode: 'stacked_progressive' },
              animationPlan: [{ step: 1, target: 'divider', effect: 'draw', durationSec: 0.35 }],
              timeline: { waitForAnimationPlan: true },
              layers: { content: true, highlight: true },
            },
          }
        : {}),
      payload: {
        label: `Concept ${idx + 1}: ${concept.title}`,
        line: { y: 'center', strokeWidth: 2.2 },
      },
    });

    const imageSrc = concept.detail?.imageUrl || concept.detail?.diagramImageUrl || null;
    const sketchPayload = buildSketchPathPayload(concept);
    const equationPayload = buildEquationPayload(concept);
    const pedagogicalDiagramPayload = buildPedagogicalDiagramPayload(concept);
    const artifactPayload = buildArtifactPayload(concept);
    if (imageSrc || sketchPayload || equationPayload || pedagogicalDiagramPayload) {
      let demoChild = null;
      if (pedagogicalDiagramPayload) {
        demoChild = {
          kind: 'PEDAGOGICAL_DIAGRAM',
          payload: pedagogicalDiagramPayload,
        };
      } else if (imageSrc) {
        demoChild = {
          kind: 'IMAGE_DRAW',
          payload: {
            src: imageSrc,
            draw: { durationSec: 2.6, showHand: true },
          },
        };
      } else if (sketchPayload) {
        demoChild = {
          kind: 'SKETCH_PATH',
          payload: sketchPayload,
        };
      } else if (equationPayload) {
        demoChild = {
          kind: 'EQUATION',
          payload: equationPayload,
        };
      }

      conceptSlides.push({
        id: textId,
        kind: 'REGION_COMPOSE',
        deps: [dividerId],
        ...(includeSceneSpec
          ? {
              scene: {
                layout: { mode: 'split', params: { left: 'narrative', right: 'demo' } },
                animationPlan: [
                  { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.25 },
                  { step: 2, target: 'left_text', effect: 'typewriter', durationSec: 0.9 },
                  { step: 3, target: 'right_demo', effect: 'draw', durationSec: 1.1 },
                ],
                timeline: { waitForAnimationPlan: true },
                physics: {
                  enabled: Boolean(sketchPayload),
                  mode: sketchPayload ? 'sketch_dynamics' : 'none',
                },
                layers: { background: true, content: true, highlight: true, transition: true },
              },
            }
          : {}),
        payload: {
          children: [
            {
              kind: 'TEXT',
              region: { xk: 0.0, yk: 0.0, wk: 0.48, hk: 1.0 },
              payload: {
                style: { font: 'sans', size: 'body', justify: 'left', vAlign: 'top' },
                text: buildNarrativeText(concept),
                typewriter: { charsPerSec: 26, cursor: true },
                ...(artifactPayload ? { artifacts: artifactPayload } : {}),
              },
            },
            {
              kind: 'BOXED_DEMO',
              startSec: 0.2,
              region: { xk: 0.52, yk: 0.05, wk: 0.48, hk: 0.9 },
              payload: {
                title: 'Demonstration',
                paddingK: 0.06,
                child: demoChild,
              },
            },
          ],
        },
      });
    } else {
      conceptSlides.push({
        id: textId,
        kind: 'TEXT',
        deps: [dividerId],
        ...(includeSceneSpec
          ? {
              scene: {
                layout: { mode: 'full_text' },
                animationPlan: [
                  { step: 1, target: 'title', effect: 'fade-in', durationSec: 0.2 },
                  { step: 2, target: 'body', effect: 'typewriter', durationSec: 1.0 },
                ],
                timeline: { waitForAnimationPlan: true },
                layers: { content: true, highlight: true },
              },
            }
          : {}),
        payload: {
          style: { font: 'sans', size: 'body', justify: 'center', vAlign: 'top' },
          text: buildNarrativeText(concept),
          typewriter: { charsPerSec: 26, cursor: true },
          ...(artifactPayload ? { artifacts: artifactPayload } : {}),
        },
      });
    }

    if (neighborhoodNodes.length > 1) {
      conceptSlides.push({
        id: focusGraphId,
        kind: 'BOXED_DEMO',
        deps: [textId],
        ...(includeSceneSpec
          ? {
              scene: {
                layout: { mode: 'canvas_based_dynamic' },
                animationPlan: [
                  { step: 1, target: 'nodes', effect: 'stagger_in', durationSec: 0.55 },
                  { step: 2, target: 'edges', effect: 'progressive_draw', durationSec: 0.95 },
                ],
                timeline: { waitForAnimationPlan: true },
                physics: { enabled: true, mode: 'force_graph' },
                layers: { background: true, content: true, highlight: true, transition: true },
              },
            }
          : {}),
        payload: {
          title: 'Dependency Demonstration',
          paddingK: 0.05,
          child: {
            kind: 'PHYSICS_DIAGRAM',
            payload: {
              title: `Dependency Focus: ${concept.title}`,
              subtitle: 'Prerequisites feed this concept, then unlock downstream ideas',
              durationSec: 4.2,
              layout: { mode: 'tree', rootId: concept.id },
              physics: {
                nodeRadius: 17,
                springK: 8.0,
                damping: 0.87,
                repulsion: 7800,
                restLength: 80,
                anchorK: 2.5,
              },
              diagram: {
                nodes: neighborhoodNodes,
                edges: neighborhoodEdges,
              },
            },
          },
        },
      });
      previousId = focusGraphId;
    } else {
      previousId = textId;
    }
  });

  const nodes = [
    {
      id: 'lesson_title',
      kind: 'TEXT',
      deps: [],
      ...(includeSceneSpec
        ? {
            scene: {
              layout: { mode: 'full_text' },
              animationPlan: [{ step: 1, target: 'title', effect: 'typewriter', durationSec: 0.7 }],
              timeline: { waitForAnimationPlan: true },
              layers: { content: true, highlight: true },
            },
          }
        : {}),
      payload: {
        style: { font: 'sans', size: 'title', justify: 'center', vAlign: 'top' },
        text: lessonTitle,
        typewriter: { charsPerSec: 20, cursor: true },
      },
    },
    {
      id: 'lesson_overview',
      kind: 'TEXT',
      deps: ['lesson_title'],
      ...(includeSceneSpec
        ? {
            scene: {
              layout: { mode: 'stacked_progressive' },
              animationPlan: [
                { step: 1, target: 'paragraph', effect: 'fade-in', durationSec: 0.3 },
                { step: 2, target: 'paragraph', effect: 'typewriter', durationSec: 0.8 },
              ],
              timeline: { waitForAnimationPlan: true },
              layers: { content: true, highlight: true },
            },
          }
        : {}),
      payload: {
        style: { font: 'sans', size: 'body', justify: 'center', vAlign: 'center' },
        text: `In this lesson, we will move from foundational ideas to advanced applications.\nUse the demonstrations to build strong intuition step by step.`,
        typewriter: { charsPerSec: 26, cursor: true },
      },
    },
    buildKnowledgeGraphSlide(concepts, lessonTitle, includeSceneSpec),
    ...conceptSlides,
    {
      id: 'lesson_conclusion',
      kind: 'TEXT',
      deps: [
        conceptSlides.length > 0
          ? conceptSlides[conceptSlides.length - 1].id
          : 'knowledge_graph_overview',
      ],
      ...(includeSceneSpec
        ? {
            scene: {
              layout: { mode: 'full_text' },
              animationPlan: [
                { step: 1, target: 'summary', effect: 'fade-in', durationSec: 0.35 },
                { step: 2, target: 'summary', effect: 'typewriter', durationSec: 0.8 },
              ],
              timeline: { waitForAnimationPlan: true },
              layers: { content: true, highlight: true, transition: true },
            },
          }
        : {}),
      payload: {
        style: { font: 'sans', size: 'body', justify: 'center', vAlign: 'center' },
        text: 'End of lesson.\nUse pause and speed controls to review each concept at your pace.',
        typewriter: { charsPerSec: 24, cursor: true },
      },
    },
  ];

  return {
    version: 1,
    lessonId,
    title: lessonTitle,
    theme: {
      board: {
        bgTop: '#0b2a20',
        bgBottom: '#061d16',
        frameAlpha: 0.1,
        grainDots: 800,
        grainAlpha: 0.05,
      },
      chalk: {
        color: 'rgba(109, 242, 255, 0.96)',
        glow: 'rgba(109, 242, 255, 0.22)',
        shadowBlur: 1.5,
        jitterPx: 0.12,
        roles: {
          diagramTitle: {
            color: 'rgba(220, 245, 255, 0.96)',
            glow: 'rgba(109, 242, 255, 0.22)',
          },
          diagramSubtitle: {
            color: 'rgba(205, 236, 250, 0.9)',
            glow: 'rgba(109, 242, 255, 0.16)',
          },
          diagramEdge: {
            color: 'rgba(109, 242, 255, 0.72)',
            glow: 'rgba(109, 242, 255, 0.2)',
          },
          diagramNodeStroke: {
            color: 'rgba(255, 211, 122, 0.96)',
            glow: 'rgba(255, 179, 71, 0.25)',
          },
          diagramNodeFill: {
            color: 'rgba(255, 183, 77, 0.12)',
            glow: 'rgba(255, 183, 77, 0.14)',
          },
          diagramLabel: {
            color: 'rgba(255, 236, 196, 0.98)',
            glow: 'rgba(255, 183, 77, 0.24)',
          },
        },
      },
      typography: {
        sans: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial',
        mono: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace',
        fluidScale: {
          title: scaled({ k: 0.048, min: 22 }),
          body: scaled({ k: 0.03, min: 16 }),
          mono: scaled({ k: 0.027, min: 14 }),
          small: scaled({ k: 0.02, min: 12 }),
          lineGap: scaled({ k: 0.048, min: 20 }),
        },
      },
      layout: {
        marginLeftK: 0.06,
        marginRightK: 0.06,
        marginTopK: 0.08,
        marginBottomK: 0.08,
      },
    },
    runtime: {
      speedFactor: 1.0,
      defaultDwellBeforeSec: 0.0,
      defaultDwellAfterSec: 2.0,
      defaultTransitionOut: includeSceneSpec ? 'fade' : 'wipe',
    },
    instructionalEvents: buildInstructionalEvents(nodes),
    nodes,
  };
}

export default knowledgeGraphToLessonSpec;
